import React, { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import QRCode from 'qrcode';
import 'primeicons/primeicons.css';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name?: string;
  is_active: boolean;
}

interface Student {
  student_id: string;
  enrollment_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  mobileNumber: string | null;
  fatherName: string | null;
  motherName: string | null;
  district: string | null;
  state: string | null;
  villageTown: string | null;
  profilePicture: { cdn_url?: string } | null;
  status: string;
  date_of_admission: string;
  is_registered: boolean;
  category: string | null;
  bloodGroup: string | null;
}

interface BatchInfo {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_name: string | null;
  course_title: string | null;
  is_active: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDOB = (dob: string | null) => {
  if (!dob) return '-';
  try {
    return new Date(dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dob; }
};

// ── ID Card Preview Component ─────────────────────────────────────────────────
interface IdCardProps {
  student: Student;
  batchInfo: BatchInfo | null;
  schoolName?: string;
  schoolAddress?: string;
  schoolPhone?: string;
  websiteUrl?: string;
  principalSign?: string;
  logoUrl?: string;
}

const IdCard: React.FC<IdCardProps> = ({
  student, batchInfo,
  schoolName = 'School Name',
  schoolAddress = 'Place your address, District State – 000000',
  schoolPhone = '',
  websiteUrl = 'hciindia.in',
  principalSign = 'ℰ𝓊𝓌',
}) => {
  const qrRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, `https://${websiteUrl}`, {
        width: 56, margin: 1,
        color: { dark: '#0e225d', light: '#ffffff' }
      });
    }
  }, [websiteUrl]);

  const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '-';

  return (
    <div className="id-card-root" style={{
      width: '320px',
      background: '#fff',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(14,34,93,0.18), 0 4px 16px rgba(14,34,93,0.10)',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      position: 'relative',
    }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0e225d 0%, #1a3a7a 50%, #234495 100%)',
        borderRadius: '20px 20px 0 0',
        // CHANGED: reduced padding-bottom from 68px to 28px
        // The avatar is now pulled up via negative margin from the body, not absolute positioned
        padding: '16px 16px 45px',
        position: 'relative',
        overflow: 'visible',
      }}>
        {/* Accent top bar */}
        <div style={{
          position: 'absolute', top: 0, left: 16, right: 16, height: 3,
          background: 'linear-gradient(90deg, #0e225d, #fbb21f)',
          borderRadius: '0 0 3px 3px', zIndex: 2,
        }} />

        {/* SVG Pattern */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '20px 20px 0 0', overflow: 'hidden', pointerEvents: 'none' }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id={`pat-${student.student_id}`} patternUnits="userSpaceOnUse" width="80" height="80">
                <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M58 50 Q55 50 55 53 L55 57 Q55 60 52 60 Q55 60 55 63 L55 67 Q55 70 58 70" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <path d="M62 50 Q65 50 65 53 L65 57 Q65 60 68 60 Q65 60 65 63 L65 67 Q65 70 62 70" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <path d="M40 30 L45 15" stroke="rgba(251,178,31,0.1)" strokeWidth="2" strokeLinecap="round"/>
                <rect x="15" y="55" width="10" height="2" fill="rgba(255,255,255,0.1)" rx="1"/>
                <rect x="15" y="60" width="10" height="2" fill="rgba(255,255,255,0.1)" rx="1"/>
                <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5"/>
                <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5"/>
                <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)"/>
                <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#pat-${student.student_id})`}/>
          </svg>
        </div>

        {/* School branding */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 2 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>
            <i className="pi pi-building" style={{ fontSize: 20, color: '#0e225d' }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.2px' }}>{schoolName}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.78)', marginTop: 4, lineHeight: 1.4 }}>{schoolAddress}</div>
          </div>
        </div>

        {/* Wave — extends beyond card width to fully cover header bottom edge corners */}
        <div style={{
          position: 'absolute', bottom: -2, left: -10, right: -10, height: 35,
          background: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0', zIndex: 10,
        }} />
      </div>

      {/* ── Body ── */}
      {/* marginTop: -2 closes the subpixel gap between header and body */}
      <div style={{ padding: '0 16px 0', textAlign: 'center', marginTop: -2 }}>

        {/* Floating Avatar — negative margin pulls it up over the wave/header boundary */}
        <div style={{
          width: 90,
          height: 90,
          borderRadius: 45,
          background: '#fff',
          padding: 3,
          boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
          margin: '-45px auto 0',   // exactly half avatar height (90/2), pulls it onto the header/body seam
          position: 'relative',
          zIndex: 20,
        }}>
          {student.profilePicture?.cdn_url ? (
            <img
              src={student.profilePicture.cdn_url}
              alt={student.name}
              style={{ width: '100%', height: '100%', borderRadius: 42, objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%', borderRadius: 42,
              background: '#fbb21f', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 26, fontWeight: 800, color: '#0e225d',
            }}>{getInitials(student.name)}</div>
          )}
          {student.is_registered && (
            <div style={{
              position: 'absolute', bottom: 2, right: 2,
              width: 18, height: 18, background: '#16a34a',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #fff',
            }}>
              <i className="pi pi-check" style={{ fontSize: 9, color: '#fff' }}></i>
            </div>
          )}
        </div>

        {/* Name */}
        <div style={{ fontSize: 18, fontWeight: 800, color: '#0e225d', letterSpacing: '-0.3px', marginBottom: 8, marginTop: 12 }}>
          {student.name}
        </div>

        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'rgba(251,178,31,0.18)', border: '1px solid rgba(251,178,31,0.4)',
          borderRadius: 8, padding: '4px 10px', marginTop: 8, marginBottom: 14,
        }}>
          <i className="pi pi-graduation-cap" style={{ fontSize: 11, color: '#fbb21f' }}></i>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fbb21f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Student · {student.enrollment_id || student.student_id.slice(-6).toUpperCase()}
          </span>
        </div>

        {/* Info rows */}
        <div style={{ textAlign: 'left', padding: '0 4px' }}>
          {[
            { icon: 'pi-user', label: "Father's Name", value: student.fatherName },
            { icon: 'pi-heart', label: "Mother's Name", value: student.motherName },
            { icon: 'pi-calendar', label: 'Date of Birth', value: formatDOB(student.dateOfBirth) },
            { icon: 'pi-phone', label: 'Contact No.', value: student.mobileNumber },
            { icon: 'pi-map-marker', label: 'Address', value: address },
          ].map((row, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 0',
              borderBottom: i < arr.length - 1 ? '1px solid #E8EEF7' : 'none',
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8, background: '#E8EEF7',
                border: '1.5px solid rgba(255,255,255,0.8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <i className={`pi ${row.icon}`} style={{ fontSize: 12, color: '#0e225d' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{row.label}</div>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: '#0e225d', marginTop: 1 }}>{row.value || '-'}</div>
              </div>
            </div>
          ))}
        </div>

        {/* QR + Signature */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 4px 4px', borderTop: '1px solid #E8EEF7', marginTop: 6,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 56, height: 56, border: '1.5px solid #E8EEF7',
              borderRadius: 6, overflow: 'hidden', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <canvas ref={qrRef} />
            </div>
            <div style={{ fontSize: 8, fontWeight: 700, color: '#0e225d', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
              {websiteUrl}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, color: '#1a3a7a', fontStyle: 'italic', lineHeight: 1, letterSpacing: '-1px', marginBottom: 2, fontWeight: 700 }}>
              {principalSign}
            </div>
            <div style={{ fontSize: 9, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Principal Sign.
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div style={{
        height: 3,
        background: 'linear-gradient(90deg, #0e225d, #fbb21f, #0e225d)',
      }} />
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const IdCardGenerator: React.FC = () => {
  // ── Operator profile store ──
  const { profile, fetchProfile } = useOperatorProfileStore();

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // ── Local state ──
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewStudent, setPreviewStudent] = useState<Student | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // School settings — seeded from store, editable by user
  const [schoolName, setSchoolName] = useState('School Name');
  const [schoolAddress, setSchoolAddress] = useState('Place your address, District State – 000000');
  const [websiteUrl, setWebsiteUrl] = useState('hciindia.in');
  const [showSettings, setShowSettings] = useState(false);

  // ── Sync store → local settings when profile loads ──
  useEffect(() => {
    if (profile) {
      setSchoolName(profile.institute_name);
      setSchoolAddress(profile.branch_name);
    }
  }, [profile]);

  const batchOptions: DropdownOption[] = batches.map(b => ({
    label: `${b.batch_name} · ${b.batch_time}`,
    value: b.batch_id,
  }));

  // ── Fetch batches ──
  useEffect(() => {
    (async () => {
      try {
        setBatchesLoading(true);
        const res = await apiClient.get('/v1/batches');
        if (res.data.success) {
          const active = res.data.data.filter((b: Batch) => b.is_active);
          setBatches(active);
          if (active.length > 0) setSelectedBatchId(active[0].batch_id);
        }
      } catch { setError('Failed to load batches'); }
      finally { setBatchesLoading(false); }
    })();
  }, []);

  // ── Fetch students when batch changes ──
  useEffect(() => {
    if (!selectedBatchId) return;
    (async () => {
      try {
        setStudentsLoading(true);
        setError(null);
        setSelectedIds(new Set());
        const res = await apiClient.get(`/v1/students/get-students-by-batch/${selectedBatchId}?status=active&limit=100`);
        if (res.data.success) {
          setStudents(res.data.data);
          setBatchInfo(res.data.batch_info);
        }
      } catch (e: any) {
        setError(e.response?.data?.error || 'Failed to load students');
        setStudents([]);
      } finally { setStudentsLoading(false); }
    })();
  }, [selectedBatchId]);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.mobileNumber || '').includes(search)
  );

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(s => s.student_id)));
  };

  const selectedStudents = filtered.filter(s => selectedIds.has(s.student_id));

  const handlePrint = (overrideCards?: Student[]) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const cards = overrideCards ?? (selectedIds.size > 0 ? filtered.filter(s => selectedIds.has(s.student_id)) : filtered);

    const cardHTML = cards.map(student => {
      const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '-';
      const initials = getInitials(student.name);
      const dob = formatDOB(student.dateOfBirth);
      return `
        <div class="card">
          <div class="card-header">
            <div class="accent-bar"></div>
            <svg class="header-pattern" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none;border-radius:20px 20px 0 0;">
              <defs>
                <pattern id="pat-${student.student_id}" patternUnits="userSpaceOnUse" width="80" height="80">
                  <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M58 50 Q55 50 55 53 L55 57 Q55 60 52 60 Q55 60 55 63 L55 67 Q55 70 58 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  <path d="M62 50 Q65 50 65 53 L65 57 Q65 60 68 60 Q65 60 65 63 L65 67 Q65 70 62 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  <path d="M40 30 L45 15" stroke="rgba(251,178,31,0.1)" stroke-width="2" stroke-linecap="round"/>
                  <rect x="15" y="55" width="10" height="2" fill="rgba(255,255,255,0.1)" rx="1"/>
                  <rect x="15" y="60" width="10" height="2" fill="rgba(255,255,255,0.1)" rx="1"/>
                  <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5"/>
                  <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5"/>
                  <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)"/>
                  <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#pat-${student.student_id})"/>
            </svg>
            <div class="school-row">
              <div class="school-logo"><i class="pi pi-building"></i></div>
              <div class="school-info">
                <div class="school-name">${schoolName}</div>
                <div class="school-sub">${schoolAddress}</div>
              </div>
            </div>
            <div class="wave"></div>
          </div>
          <div class="card-body">
            <div class="avatar-wrap">
              ${student.profilePicture?.cdn_url
                ? `<img src="${student.profilePicture.cdn_url}" class="avatar-img" />`
                : `<div class="avatar-initials">${initials}</div>`
              }
            </div>
            <div class="student-name">${student.name}</div>
            <div class="badge"><i class="pi pi-graduation-cap"></i> Student · ${student.enrollment_id || student.student_id.slice(-6).toUpperCase()}</div>
            <div class="info-list">
              <div class="info-row"><div class="info-icon"><i class="pi pi-user"></i></div><div><div class="info-label">Father's Name</div><div class="info-value">${student.fatherName || '-'}</div></div></div>
              <div class="info-row"><div class="info-icon"><i class="pi pi-heart"></i></div><div><div class="info-label">Mother's Name</div><div class="info-value">${student.motherName || '-'}</div></div></div>
              <div class="info-row"><div class="info-icon"><i class="pi pi-calendar"></i></div><div><div class="info-label">Date of Birth</div><div class="info-value">${dob}</div></div></div>
              <div class="info-row"><div class="info-icon"><i class="pi pi-phone"></i></div><div><div class="info-label">Contact No.</div><div class="info-value">${student.mobileNumber || '-'}</div></div></div>
              <div class="info-row last"><div class="info-icon"><i class="pi pi-map-marker"></i></div><div><div class="info-label">Address</div><div class="info-value">${address}</div></div></div>
            </div>
            <div class="footer-row">
              <div class="qr-placeholder">
                <div class="qr-box" id="qr-${student.student_id}"></div>
                <div class="qr-url">${websiteUrl}</div>
              </div>
              <div class="sig-block">
                <div class="sig-script">ℰ𝓊𝓌</div>
                <div class="sig-label">Principal Sign.</div>
              </div>
            </div>
          </div>
          <div class="bottom-strip"></div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head><title>ID Cards</title>
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <link href="https://cdnjs.cloudflare.com/ajax/libs/primeicons/7.0.0/primeicons.min.css" rel="stylesheet">
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      body{font-family:'Plus Jakarta Sans',sans-serif;background:#f0f4fa;padding:20px}
      .grid{display:flex;flex-wrap:wrap;gap:20px;justify-content:center}
      .card{width:320px;background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 8px 30px rgba(14,34,93,0.15);position:relative;page-break-inside:avoid}
      .card-header{background:linear-gradient(135deg,#0e225d 0%,#1a3a7a 50%,#234495 100%) !important;border-radius:20px 20px 0 0;padding:16px 16px 45px;position:relative;overflow:hidden}
      .accent-bar{position:absolute;top:0;left:16px;right:16px;height:3px;background:linear-gradient(90deg,#0e225d,#fbb21f) !important;border-radius:0 0 3px 3px;z-index:2}
      .wave{position:absolute;bottom:-2px;left:-10px;right:-10px;height:35px;background:#fff !important;border-radius:50% 50% 0 0/100% 100% 0 0;z-index:10}
      .school-row{display:flex;align-items:center;gap:10px;position:relative;z-index:2}
      .school-logo{width:44px;height:44px;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 8px rgba(0,0,0,0.2)}
      .school-logo .pi{font-size:20px;color:#0e225d}
      .school-name{font-size:15px;font-weight:800;color:#fff;line-height:1.2}
      .school-sub{font-size:10px;color:rgba(255,255,255,0.78);margin-top:4px;line-height:1.4}
      .card-body{padding:0 16px 0;text-align:center;margin-top:-2px}
      .avatar-wrap{width:90px;height:90px;border-radius:45px;background:#fff !important;padding:3px;box-shadow:0 4px 16px rgba(0,0,0,0.22);margin:-45px auto 0;position:relative;z-index:20}
      .avatar-img{width:100%;height:100%;border-radius:42px;object-fit:cover}
      .avatar-initials{width:100%;height:100%;border-radius:42px;background:#fbb21f !important;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800;color:#0e225d}
      .student-name{font-size:18px;font-weight:800;color:#0e225d;letter-spacing:-0.3px;margin-bottom:8px;margin-top:12px}
      .badge{display:inline-flex;align-items:center;gap:5px;background:rgba(251,178,31,0.18) !important;border:1px solid rgba(251,178,31,0.4);border-radius:8px;padding:4px 10px;margin-top:8px;margin-bottom:14px;font-size:10px;font-weight:700;color:#fbb21f;text-transform:uppercase;letter-spacing:0.5px}
      .badge .pi{font-size:11px;color:#fbb21f}
      .info-list{text-align:left;padding:0 4px}
      .info-row{display:flex;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid #E8EEF7}
      .info-row.last{border-bottom:none}
      .info-icon{width:30px;height:30px;border-radius:8px;background:#E8EEF7 !important;display:flex;align-items:center;justify-content:center;flex-shrink:0}
      .info-icon .pi{font-size:12px;color:#0e225d}
      .info-label{font-size:8px;font-weight:500;color:#6b7280;text-transform:uppercase;letter-spacing:0.4px}
      .info-value{font-size:10.5px;font-weight:600;color:#0e225d;margin-top:1px}
      .footer-row{display:flex;align-items:center;justify-content:space-between;padding:10px 4px 4px;border-top:1px solid #E8EEF7;margin-top:6px}
      .qr-placeholder{display:flex;flex-direction:column;align-items:center;gap:4px}
      .qr-box{width:56px;height:56px;border:1.5px solid #E8EEF7;border-radius:6px;overflow:hidden;background:#fff !important}
      .qr-url{font-size:8px;font-weight:700;color:#0e225d;letter-spacing:0.6px;text-transform:uppercase}
      .sig-block{text-align:right}
      .sig-script{font-size:24px;color:#1a3a7a;font-style:italic;line-height:1;letter-spacing:-1px;margin-bottom:2px;font-weight:700}
      .sig-label{font-size:9px;font-weight:500;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px}
      .bottom-strip{height:3px;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important}
      @media print{body{background:white !important;padding:0}.grid{gap:10px}@page{margin:10mm}}
    </style></head><body>
    <div class="grid">${cardHTML}</div>
    <script>
      window.onload = function() {
        document.querySelectorAll('[id^="qr-"]').forEach(function(el) {
          new QRCode(el, {text:"https://${websiteUrl}",width:56,height:56,colorDark:"#0e225d",colorLight:"#ffffff",correctLevel:QRCode.CorrectLevel.M});
        });
        setTimeout(function(){ window.print(); }, 800);
      };
    <\/script></body></html>`);
    printWindow.document.close();
  };

  // ── Shared styles ──
  const col: React.CSSProperties = { color: '#9ca3af', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' };
  const cardStyle: React.CSSProperties = {
    background: 'white', borderRadius: 16,
    boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB',
  };

  if (batchesLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, background: 'white', borderRadius: 16, border: '1px solid #E5E7EB' }}>
      <div style={{ textAlign: 'center' }}>
        <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ marginTop: 16, color: '#0e225d', fontSize: '0.95rem', fontWeight: 500 }}>Loading...</p>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .idc-student-row:hover { background: rgba(14,34,93,0.03) !important; }
        .idc-student-row { transition: background 0.15s ease; }
        .idc-checkbox { accent-color: #0e225d; width: 16px; height: 16px; cursor: pointer; border-radius: 4px; }
        .idc-search::placeholder { color: #c4cdd8; }
        .idc-search:focus { outline: none; border-color: #0e225d !important; box-shadow: 0 0 0 3px rgba(14,34,93,0.08); }
      `}</style>

      <div style={{ padding: 0, margin: '0 auto' }}>

        {/* ── Hero Header ── */}
        <div style={{
          background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
          borderRadius: 20, padding: 32, marginBottom: 24,
          color: 'white', position: 'relative', overflow: 'hidden',
          boxShadow: '2px 0 12px rgba(14,34,93,0.3)',
        }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', background: 'rgba(251,178,31,0.18)',
              border: '1px solid rgba(251,178,31,0.35)', borderRadius: 20,
              marginBottom: 12, fontSize: '0.68rem', fontWeight: 700,
              letterSpacing: '0.8px', textTransform: 'uppercase', color: '#fbb21f',
            }}>
              <i className="pi pi-id-card" style={{ fontSize: '0.7rem' }}></i>
              ID Card Generator
            </div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: 700, color: 'white', letterSpacing: '-0.5px' }}>
              Student ID Cards
            </h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
              {batchInfo ? `${batchInfo.course_name} — ${batchInfo.batch_name}` : 'Select a batch to generate ID cards'}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: 20, width: '100%' }} />}

        {/* ── Batch + Settings row ── */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {/* Batch selector */}
          <div style={{ ...cardStyle, padding: '20px 24px', flex: 2, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 4, height: 18, background: 'linear-gradient(180deg, #fbb21f, #e09a00)', borderRadius: 4 }} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0e225d' }}>
                <i className="pi pi-th-large" style={{ fontSize: '0.8rem', marginRight: 6 }}></i>
                Select Batch
              </span>
            </div>
            <GlobalDropdownField
              value={selectedBatchId}
              onChange={(val) => setSelectedBatchId(val)}
              options={batchOptions}
              placeholder="Select a batch"
              icon="pi pi-users"
            />
          </div>

          {/* School settings */}
          <div style={{ ...cardStyle, padding: '20px 24px', flex: 3, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 4, height: 18, background: 'linear-gradient(180deg, #fbb21f, #e09a00)', borderRadius: 4 }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0e225d' }}>
                  <i className="pi pi-cog" style={{ fontSize: '0.8rem', marginRight: 6 }}></i>
                  Card Settings
                </span>
              </div>
              <button
                onClick={() => setShowSettings(v => !v)}
                style={{
                  padding: '5px 12px', background: showSettings ? 'rgba(14,34,93,0.08)' : 'transparent',
                  border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600, color: '#0e225d', transition: 'all 0.2s',
                }}
              >
                {showSettings ? 'Hide' : 'Edit'} Settings
              </button>
            </div>
            {showSettings ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'School Name', value: schoolName, set: setSchoolName, icon: 'pi-building' },
                  { label: 'Address', value: schoolAddress, set: setSchoolAddress, icon: 'pi-map-marker' },
                  { label: 'Website URL', value: websiteUrl, set: setWebsiteUrl, icon: 'pi-globe' },
                ].map(f => (
                  <div key={f.label}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <i className={`pi ${f.icon}`} style={{ fontSize: '0.72rem' }}></i> {f.label}
                    </label>
                    <input
                      value={f.value}
                      onChange={e => f.set(e.target.value)}
                      className="idc-search"
                      style={{
                        width: '100%', padding: '8px 12px',
                        border: '1.5px solid #E5E7EB', borderRadius: 8,
                        fontSize: '0.83rem', color: '#0e225d', fontWeight: 500,
                        fontFamily: 'inherit', transition: 'all 0.2s',
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                  { label: 'Institute', val: schoolName },
                  { label: 'Branch', val: schoolAddress },
                  { label: 'URL', val: websiteUrl },
                ].map(i => (
                  <div key={i.label} style={{ padding: '6px 12px', background: '#E8EEF7', borderRadius: 8 }}>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>{i.label}</div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0e225d', marginTop: 2 }}>{i.val}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Student selection table ── */}
        {selectedBatchId && (
          <div style={{ ...cardStyle, marginBottom: 20, overflow: 'hidden' }}>
            {/* Toolbar */}
            <div style={{
              padding: '16px 24px', borderBottom: '2px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 4, height: 22, background: 'linear-gradient(180deg, #fbb21f, #e09a00)', borderRadius: 4 }} />
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0e225d' }}>
                  Students
                  {students.length > 0 && (
                    <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af' }}>({students.length})</span>
                  )}
                </h2>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Search */}
                <div style={{ position: 'relative' }}>
                  <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: '#9ca3af' }}></i>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search students..."
                    className="idc-search"
                    style={{
                      paddingLeft: 32, paddingRight: 12, height: 36,
                      border: '1.5px solid #E5E7EB', borderRadius: 8,
                      fontSize: '0.82rem', color: '#0e225d', fontFamily: 'inherit',
                      width: 200, transition: 'all 0.2s',
                    }}
                  />
                </div>
                {/* Print button */}
                <button
                  onClick={() => handlePrint()}
                  disabled={studentsLoading || filtered.length === 0}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '0 18px', height: 36,
                    background: (studentsLoading || filtered.length === 0) ? 'rgba(14,34,93,0.3)' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                    border: 'none', borderRadius: 10, color: 'white',
                    fontSize: '0.8rem', fontWeight: 700, cursor: (studentsLoading || filtered.length === 0) ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 8px rgba(14,34,93,0.25)', letterSpacing: '0.3px',
                  }}
                >
                  <i className="pi pi-print" style={{ fontSize: '0.8rem' }}></i>
                  {selectedIds.size > 0 ? `Print ${selectedIds.size} Selected` : 'Print All'}
                </button>
              </div>
            </div>

            {studentsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="3" animationDuration=".8s" />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, background: 'rgba(14,34,93,0.06)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="pi pi-users" style={{ fontSize: '1.6rem', color: '#0e225d', opacity: 0.3 }}></i>
                </div>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>No students found</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...col, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>
                      <input
                        type="checkbox"
                        className="idc-checkbox"
                        checked={selectedIds.size === filtered.length && filtered.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    {['Student', 'Father', 'DOB', 'Contact', 'Admission', 'Preview'].map(h => (
                      <th key={h} style={{ ...col, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(student => (
                    <tr key={student.student_id} className="idc-student-row">
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                          type="checkbox"
                          className="idc-checkbox"
                          checked={selectedIds.has(student.student_id)}
                          onChange={() => toggleSelect(student.student_id)}
                        />
                      </td>
                      {/* Student */}
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ position: 'relative' }}>
                            <Avatar
                              image={student.profilePicture?.cdn_url || undefined}
                              icon={!student.profilePicture ? 'pi pi-user' : undefined}
                              size="normal" shape="circle"
                              style={{ width: 36, height: 36, background: student.profilePicture ? 'transparent' : '#E8EEF7', color: '#0e225d', border: '2px solid #E5E7EB' }}
                            />
                            {student.is_registered && (
                              <div style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, background: '#16a34a', borderRadius: '50%', border: '2px solid white' }} />
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#0e225d' }}>{student.name}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{student.enrollment_id || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.fatherName || '—'}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{formatDOB(student.dateOfBirth)}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.mobileNumber || '—'}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.date_of_admission || '—'}</td>
                      {/* Preview */}
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <button
                          onClick={() => setPreviewStudent(student)}
                          title="Preview ID Card"
                          style={{
                            width: 32, height: 32, background: 'transparent',
                            border: '1px solid transparent', borderRadius: 8, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#9ca3af', transition: 'all 0.2s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(14,34,93,0.08)'; e.currentTarget.style.color = '#0e225d'; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = 'transparent'; }}
                        >
                          <i className="pi pi-id-card" style={{ fontSize: '0.85rem' }}></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Footer count */}
            {!studentsLoading && filtered.length > 0 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                  {selectedIds.size > 0
                    ? <><strong style={{ color: '#0e225d' }}>{selectedIds.size}</strong> selected of {filtered.length}</>
                    : <>{filtered.length} students — <span style={{ color: '#fbb21f', fontWeight: 600 }}>select rows to print specific cards</span></>
                  }
                </span>
                {selectedIds.size > 0 && (
                  <button onClick={() => setSelectedIds(new Set())} style={{ fontSize: '0.75rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Clear selection
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* No batch selected */}
        {!selectedBatchId && batches.length > 0 && (
          <div style={{ ...cardStyle, padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, background: 'rgba(14,34,93,0.06)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="pi pi-id-card" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }}></i>
            </div>
            <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '1.2rem', fontWeight: 700 }}>Select a Batch</h3>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>Choose a batch above to load students and generate ID cards</p>
          </div>
        )}
      </div>

      {/* ── Preview Dialog ── */}
      {previewStudent && (
        <Dialog
          visible={!!previewStudent}
          onHide={() => setPreviewStudent(null)}
          style={{ width: 'auto' }}
          dismissableMask
          header={false}
          pt={{
            root: { style: { borderRadius: 20, overflow: 'hidden', border: '1px solid #E5E7EB', boxShadow: '0 20px 60px rgba(14,34,93,0.2)', background: 'transparent' } },
            header: { style: { display: 'none' } },
            content: { style: { padding: 24, background: '#E8EEF7', borderRadius: 20 } },
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0e225d' }}>
                <i className="pi pi-id-card" style={{ marginRight: 6 }}></i>
                ID Card Preview
              </span>
              <button
                onClick={() => setPreviewStudent(null)}
                style={{ background: 'rgba(14,34,93,0.08)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0e225d' }}
              >
                <i className="pi pi-times" style={{ fontSize: '0.8rem' }}></i>
              </button>
            </div>
            <IdCard
              student={previewStudent}
              batchInfo={batchInfo}
              schoolName={schoolName}
              schoolAddress={schoolAddress}
              websiteUrl={websiteUrl}
            />
            <button
              onClick={() => {
                const student = previewStudent;
                setPreviewStudent(null);
                handlePrint([student]);
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 24px',
                background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: 'none', borderRadius: 10, color: 'white',
                fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(14,34,93,0.3)',
              }}
            >
              <i className="pi pi-print" style={{ fontSize: '0.85rem' }}></i>
              Print This Card
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default IdCardGenerator;